/**
 * The app-side emit host for the contact-sales callback modal
 * (`@heidi/commerce`).
 *
 * Ruling 24: the domain side decides WHEN each event fires and builds every
 * `intl`-formatted label; these two functions add the `event_name`, the
 * destination routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: the view passes `{ amplitude: false }` and
 * the three clicks pass none. Neither `modal_viewed` nor `button_clicked` is on
 * the Snowplow denylist (`@/lib/analytics/snowplow-suppression`), so no emit here
 * carries suppression and none should acquire one.
 *
 * MODULE-LEVEL FUNCTIONS ON PURPOSE. `trackTrialUpgradeRequestCallbackViewed`
 * ends up a dependency of the modal's open effect, so an emit whose identity
 * changed per render would re-fire it and double-count the view.
 */
import { track, TrackingEventName } from '@heidi/analytics/events';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import {
  TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION,
  type TrackTrialUpgradeRequestCallbackClick,
  type TrackTrialUpgradeRequestCallbackViewed,
  type TrialUpgradeRequestCallbackActionToken,
  type TrialUpgradeRequestCallbackAnalyticsPorts,
} from '@heidi/commerce/contracts';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from './trialsAndUpgradesScreenNames';

const BUTTON_ACTION_BY_TOKEN = {
  [TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.send]:
    ButtonAction.TrialUpgradeRequestCallbackSend,
  [TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.cancel]:
    ButtonAction.TrialUpgradeRequestCallbackCancel,
  [TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.back]:
    ButtonAction.TrialUpgradeRequestCallbackBack,
} as const satisfies Record<TrialUpgradeRequestCallbackActionToken, ButtonAction>;

/** The callback modal became visible. */
export const trackTrialUpgradeRequestCallbackViewed: TrackTrialUpgradeRequestCallbackViewed =
  ({ screen_name }) => {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name] },
    });
  };

/** One of the callback modal's three CTAs was clicked. */
export const trackTrialUpgradeRequestCallbackClick: TrackTrialUpgradeRequestCallbackClick =
  ({ screen_name, action, ...rest }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        action: BUTTON_ACTION_BY_TOKEN[action],
        ...rest,
      },
    });
  };

/**
 * The two seams as one bundle.
 *
 * A module constant rather than a `useMemo`, so it is the same object on every
 * render and cannot be the thing that re-fires the open effect.
 */
export const trialUpgradeRequestCallbackAnalyticsPorts: TrialUpgradeRequestCallbackAnalyticsPorts =
  {
    trackViewed: trackTrialUpgradeRequestCallbackViewed,
    trackClick: trackTrialUpgradeRequestCallbackClick,
  };
